import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Music, Youtube, Flame, ExternalLink, Disc3, Play, X, Music2, Tv } from "lucide-react";
import { Link } from "react-router-dom";
import { api, formatError } from "../lib/api";
import { Screen, PageHeader, Empty } from "../components/ui-kit";

const TABS = [
  { key: "youtube", label: "YouTube", icon: Youtube, color: "#FF4D4D" },
  { key: "spotify", label: "Spotify", icon: Music, color: "#1DB954" },
];

export default function DjBoard() {
  const [tab, setTab] = useState("youtube");
  const [board, setBoard] = useState({ youtube: [], spotify: [] });
  const [nowPlaying, setNowPlaying] = useState(null);
  const [history, setHistory] = useState([]);

  const load = () => {
    api.get("/song-requests/board").then((r) => setBoard(r.data)).catch(() => {});
    api.get("/song-requests/now-playing").then((r) => setNowPlaying(r.data)).catch(() => {});
    api.get("/song-requests/history").then((r) => setHistory(r.data)).catch(() => {});
  };
  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, []);

  const markPlayed = async (key) => {
    try { await api.post("/song-requests/played", { url_key: key }); toast.success("Now spinning — dropped off the board"); load(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const clearPlaying = async () => {
    try { await api.delete("/song-requests/now-playing"); load(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const list = board[tab] || [];
  const active = TABS.find((t) => t.key === tab);

  return (
    <Screen>
      <PageHeader overline="For the DJ" title="Dedication Board" />
      <div className="-mt-2 mb-4 flex items-center justify-between gap-3 flex-wrap">
        <p className="text-[#A19DB0] text-sm">Anonymous crowd requests, ranked by how many people asked. Tap ▶ to spin a song and drop it off the board.</p>
        <Link to="/party" data-testid="party-mode-btn" className="inline-flex items-center gap-2 rounded-full bg-[#D4AF37] text-black px-4 py-2 text-sm font-semibold shrink-0 active:scale-95 transition-transform"><Tv size={16} /> Party Mode (TV)</Link>
      </div>

      <Link to="/request-song" data-testid="dj-request-song-link" className="flex items-center justify-between gap-3 rounded-2xl border border-[#9D4EDD]/40 bg-[#9D4EDD]/10 p-4 mb-5 active:scale-[0.99] transition-transform">
        <span className="flex items-center gap-3"><span className="w-10 h-10 rounded-full bg-[#9D4EDD]/20 flex items-center justify-center shrink-0"><Music2 className="text-[#c79bff]" size={18} /></span>
          <span><span className="block font-medium text-sm">Request a Song</span><span className="block text-[#A19DB0] text-xs">Open the crowd request page &amp; billboard</span></span></span>
        <ExternalLink size={16} className="text-[#A19DB0] shrink-0" />
      </Link>

      {nowPlaying && (
        <div data-testid="now-playing-banner" className="rounded-2xl border border-[#D4AF37]/40 bg-[#D4AF37]/[0.08] p-4 mb-5 flex items-center gap-3">
          <span className="w-10 h-10 rounded-full bg-[#D4AF37]/20 flex items-center justify-center shrink-0"><Disc3 className="text-[#D4AF37]" size={20} /></span>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-wide text-[#D4AF37] font-semibold">Now spinning</p>
            <a href={nowPlaying.url} target="_blank" rel="noreferrer" data-testid="now-playing-title" className="text-sm font-medium truncate block">{nowPlaying.title || nowPlaying.url}</a>
          </div>
          <button onClick={clearPlaying} data-testid="clear-now-playing" className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-[#A19DB0] hover:text-white shrink-0"><X size={15} /></button>
        </div>
      )}

      <div className="flex gap-2 mb-5" data-testid="dj-board-tabs">
        {TABS.map((t) => {
          const Icon = t.icon; const on = tab === t.key;
          return (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`dj-tab-${t.key}`}
              className={`flex-1 flex items-center justify-center gap-2 rounded-full py-2.5 text-sm font-semibold border transition-colors ${on ? "text-black border-transparent" : "bg-white/5 border-white/10 text-[#A19DB0]"}`}
              style={on ? { background: t.color } : undefined}>
              <Icon size={16} /> {t.label} <span className="opacity-70">({(board[t.key] || []).length})</span>
            </button>
          );
        })}
      </div>

      {list.length === 0 ? <Empty icon={Disc3} text={`No ${active?.label} requests yet.`} /> : (
        <div className="space-y-2" data-testid={`dj-list-${tab}`}>
          {list.map((s, i) => (
            <div key={s.url_key} data-testid={`dj-song-${i}`}
              className="flex items-center gap-3 rounded-2xl bg-white/5 border border-white/10 p-3"
              style={i === 0 ? { borderColor: `${active?.color}66`, background: `${active?.color}14` } : undefined}>
              <span className="w-8 h-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-sm font-bold shrink-0" style={i === 0 ? { color: active?.color } : undefined}>{i + 1}</span>
              <a href={s.url} target="_blank" rel="noreferrer" className="flex-1 min-w-0">
                <p className="text-sm truncate font-medium">{s.title || s.url}</p>
                <p className="text-[#A19DB0] text-xs truncate flex items-center gap-1"><ExternalLink size={11} /> {s.url}</p>
              </a>
              <span data-testid={`dj-count-${i}`} className="flex items-center gap-1 text-sm font-bold px-2.5 py-1 rounded-full shrink-0" style={{ background: `${active?.color}22`, color: active?.color }}>
                <Flame size={14} /> {s.count}
              </span>
              <button onClick={() => markPlayed(s.url_key)} data-testid={`dj-play-${i}`} title="Mark as now playing"
                className="w-9 h-9 rounded-full bg-[#D4AF37] text-black flex items-center justify-center shrink-0 active:scale-90 transition-transform"><Play size={16} fill="currentColor" /></button>
            </div>
          ))}
        </div>
      )}

      {history.filter((h) => h.url_key !== nowPlaying?.url_key).length > 0 && (
        <div className="mt-8" data-testid="dj-history">
          <h2 className="text-[11px] uppercase tracking-wide text-[#A19DB0] font-semibold mb-2">Just played</h2>
          <div className="space-y-1.5">
            {history.filter((h) => h.url_key !== nowPlaying?.url_key).map((h) => (
              <a key={h.url_key} href={h.url} target="_blank" rel="noreferrer" data-testid={`dj-history-${h.url_key}`} className="flex items-center gap-2.5 rounded-xl bg-white/[0.03] border border-white/10 px-3 py-2 opacity-80 hover:opacity-100 transition-opacity">
                <Music2 size={14} className="text-[#A19DB0] shrink-0" />
                <p className="text-xs truncate flex-1">{h.title || h.url}</p>
                <span className="text-[10px] text-[#A19DB0] capitalize shrink-0">{h.platform}</span>
              </a>
            ))}
          </div>
        </div>
      )}
    </Screen>
  );
}
